import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Divider, IconButton, Tooltip, Paper, Chip,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Menu, MenuList, MenuItem as MenuItemComponent,
  Alert, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, RadioGroup, Radio, FormLabel
} from '@mui/material';
import {
  Preview as PreviewIcon, Code as CodeIcon, Add as AddIcon,
  Person as PersonIcon, Today as DateIcon, Business as BusinessIcon,
  Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon,
  ExpandMore as ExpandMoreIcon, Visibility as ViewIcon,
  VisibilityOff as HideIcon, ContentCopy as CopyIcon,
  Upload as UploadIcon, Description as DocIcon,
  PictureAsPdf as PdfIcon, AutoAwesome as AutoIcon
} from '@mui/icons-material';
import client from "../../../../API"; // Adjust to your API client location

// Enhanced Contract Template Dialog with Document Upload & Simple Sign
const EnhancedContractTemplateDialog = ({ open, template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    js_content: '',
    expires_after_days: '7',
    requires_resignation_on_update: true,
    contract_type: 'simple' // 'simple' or 'complex'
  });
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('split'); // 'split', 'code', 'preview'
  const [signatureSpots, setSignatureSpots] = useState([]);
  const [showToolbar, setShowToolbar] = useState(true);
  const [insertMenuAnchor, setInsertMenuAnchor] = useState(null);
  const [showSpotDetails, setShowSpotDetails] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [processingDocument, setProcessingDocument] = useState(false);
  const [documentText, setDocumentText] = useState('');
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        js_content: template.contract_body || '',
        expires_after_days: template.expires_after_days || '',
        requires_resignation_on_update: template.requires_resignation_on_update ?? true,
        contract_type: template.contract_type || 'complex'
      });
      extractSignatureSpots(template.contract_body || '');
    } else {
      const defaultTemplate = getDefaultTemplate('complex');
      setFormData(prev => ({ ...prev, js_content: defaultTemplate }));
      extractSignatureSpots(defaultTemplate);
    }
  }, [template]);

  const getDefaultTemplate = (type) => {
    if (type === 'simple') {
      return `<div className="simple-contract" style={{fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '40px', lineHeight: '1.6'}}>
  <div className="header" style={{textAlign: 'center', marginBottom: '30px'}}>
    <h1 style={{color: '#333', marginBottom: '10px'}}>{{CONTRACT_TITLE}}</h1>
    <p style={{color: '#666', fontSize: '14px'}}>Terms of Service Agreement</p>
  </div>

  <div className="content" style={{marginBottom: '30px'}}>
    <h3>Agreement Terms</h3>
    <p>By clicking "I Agree" below, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions:</p>
    
    <ul>
      <li>You agree to comply with all company policies and procedures.</li>
      <li>You understand your rights and responsibilities as outlined in this agreement.</li>
      <li>This agreement is effective immediately upon acceptance.</li>
      <li>You may withdraw your agreement with proper notice as outlined in company policy.</li>
    </ul>

    <div style={{margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
      <strong>Company:</strong> {{COMPANY_NAME}}<br/>
      <strong>Employee:</strong> {{EMPLOYEE_NAME}}<br/>
      <strong>Date:</strong> {{SIGNATURE_DATE}}
    </div>
  </div>

  <div className="agreement-section" style={{marginTop: '40px', textAlign: 'center', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '5px'}}>
    <p style={{marginBottom: '15px', fontWeight: 'bold'}}>By proceeding, you agree to all terms above.</p>
    {{SIMPLE_AGREEMENT_BUTTON}}
  </div>
</div>`;
    } else {
      return `<div className="contract-document" style={{fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '40px', lineHeight: '1.6'}}>
  <div className="header" style={{textAlign: 'center', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px'}}>
    <h1 style={{color: '#333', marginBottom: '10px'}}>{{CONTRACT_TITLE}}</h1>
    <p style={{color: '#666', fontSize: '14px'}}>Contract Agreement</p>
  </div>

  <div className="content" style={{marginBottom: '40px'}}>
    <p>This agreement is made between:</p>
    
    <div style={{margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
      <strong>Company:</strong> {{COMPANY_NAME}}<br/>
      <strong>Employee:</strong> {{EMPLOYEE_NAME}}<br/>
      <strong>Date:</strong> {{SIGNATURE_DATE}}
    </div>

    <h3>Terms and Conditions</h3>
    <p>The following terms and conditions apply to this agreement:</p>
    
    <ol>
      <li>This contract is effective immediately upon signature.</li>
      <li>All parties agree to the terms outlined herein.</li>
      <li>This agreement may be terminated with proper notice.</li>
    </ol>

    <h3>Employment Details</h3>
    <p><strong>Position:</strong> {{POSITION}}</p>
    <p><strong>Department:</strong> {{DEPARTMENT}}</p>
    <p><strong>Start Date:</strong> {{START_DATE}}</p>
  </div>

  <div className="signature-section" style={{marginTop: '60px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
    {{SIGNATURE_SPOT:Employee Signature:employee}}
    {{SIGNATURE_SPOT:Manager Signature:manager}}
  </div>
</div>`;
    }
  };

  const extractSignatureSpots = (content) => {
    const spots = [];
    const regex = /\{\{SIGNATURE_SPOT:([^:]+):([^}]+)\}\}/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      spots.push({
        id: match[2],
        label: match[1],
        type: 'signature'
      });
    }
    setSignatureSpots(spots);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setProcessingDocument(true);
    
    try {
      const text = await processDocument(file);
      setDocumentText(text);
      const htmlContent = convertTextToHtml(text);
      
      // Create a complete template from the extracted content
      const templateContent = createTemplateFromContent(htmlContent, formData.contract_type);
      setFormData(prev => ({ ...prev, js_content: templateContent }));
      extractSignatureSpots(templateContent);
      
      setUploadMode(false);
    } catch (error) {
      console.error('Error processing document:', error);
      alert('Failed to process document. Please try again.');
    } finally {
      setProcessingDocument(false);
    }
  };

  const processDocument = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          let text = '';
          
          if (file.type === 'application/pdf') {
            // For PDF processing, you'd typically use pdf.js or a similar library
            // This is a simplified version
            text = await extractPdfText(e.target.result);
          } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                     file.type === 'application/msword') {
            // For Word docs, you'd use mammoth.js or similar
            text = await extractWordText(e.target.result);
          } else if (file.type === 'text/plain') {
            text = e.target.result;
          } else {
            throw new Error('Unsupported file type');
          }
          
          resolve(text);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      
      if (file.type === 'text/plain') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const extractPdfText = async (arrayBuffer) => {
    // Simplified PDF text extraction
    // In a real implementation, use pdf.js:
    // const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    // Extract text from all pages
    return "PDF text extraction not fully implemented. Please paste text directly.";
  };

  const extractWordText = async (arrayBuffer) => {
    // Simplified Word text extraction
    // In a real implementation, use mammoth.js:
    // const result = await mammoth.extractRawText({arrayBuffer});
    // return result.value;
    return "Word text extraction not fully implemented. Please paste text directly.";
  };

  const convertTextToHtml = (text) => {
    // Convert plain text to basic HTML
    return text
      .split('\n\n')
      .map(paragraph => {
        if (paragraph.trim() === '') return '';
        
        // Check if it looks like a heading (short line, possibly all caps or title case)
        if (paragraph.length < 100 && (
          paragraph === paragraph.toUpperCase() ||
          /^[A-Z][a-z\s]+$/.test(paragraph.trim())
        )) {
          return `<h3>${paragraph.trim()}</h3>`;
        }
        
        // Check for numbered lists
        if (/^\d+\./.test(paragraph.trim())) {
          const items = paragraph.split(/\n(?=\d+\.)/);
          const listItems = items.map(item => 
            `<li>${item.replace(/^\d+\.\s*/, '').trim()}</li>`
          ).join('\n      ');
          return `<ol>\n      ${listItems}\n    </ol>`;
        }
        
        // Check for bullet points
        if (/^[-•*]/.test(paragraph.trim())) {
          const items = paragraph.split(/\n(?=[-•*])/);
          const listItems = items.map(item => 
            `<li>${item.replace(/^[-•*]\s*/, '').trim()}</li>`
          ).join('\n      ');
          return `<ul>\n      ${listItems}\n    </ul>`;
        }
        
        // Regular paragraph
        return `<p>${paragraph.trim()}</p>`;
      })
      .filter(p => p !== '')
      .join('\n\n    ');
  };

  const createTemplateFromContent = (htmlContent, contractType) => {
    if (contractType === 'simple') {
      return `<div className="simple-contract" style={{fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '40px', lineHeight: '1.6'}}>
  <div className="header" style={{textAlign: 'center', marginBottom: '30px'}}>
    <h1 style={{color: '#333', marginBottom: '10px'}}>{{CONTRACT_TITLE}}</h1>
    <p style={{color: '#666', fontSize: '14px'}}>Agreement</p>
  </div>

  <div className="content" style={{marginBottom: '30px'}}>
    ${htmlContent}

    <div style={{margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
      <strong>Company:</strong> {{COMPANY_NAME}}<br/>
      <strong>Employee:</strong> {{EMPLOYEE_NAME}}<br/>
      <strong>Date:</strong> {{SIGNATURE_DATE}}
    </div>
  </div>

  <div className="agreement-section" style={{marginTop: '40px', textAlign: 'center', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '5px'}}>
    <p style={{marginBottom: '15px', fontWeight: 'bold'}}>By proceeding, you agree to all terms above.</p>
    {{SIMPLE_AGREEMENT_BUTTON}}
  </div>
</div>`;
    } else {
      return `<div className="contract-document" style={{fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '40px', lineHeight: '1.6'}}>
  <div className="header" style={{textAlign: 'center', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px'}}>
    <h1 style={{color: '#333', marginBottom: '10px'}}>{{CONTRACT_TITLE}}</h1>
    <p style={{color: '#666', fontSize: '14px'}}>Contract Agreement</p>
  </div>

  <div className="content" style={{marginBottom: '40px'}}>
    ${htmlContent}

    <div style={{margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
      <strong>Company:</strong> {{COMPANY_NAME}}<br/>
      <strong>Employee:</strong> {{EMPLOYEE_NAME}}<br/>
      <strong>Date:</strong> {{SIGNATURE_DATE}}
    </div>
  </div>

  <div className="signature-section" style={{marginTop: '60px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
    {{SIGNATURE_SPOT:Employee Signature:employee}}
    {{SIGNATURE_SPOT:Manager Signature:manager}}
  </div>
</div>`;
    }
  };

  const handlePasteText = () => {
    const text = prompt('Paste your contract text here:', '');
    if (text && text.trim()) {
      const htmlContent = convertTextToHtml(text);
      const templateContent = createTemplateFromContent(htmlContent, formData.contract_type);
      setFormData(prev => ({ ...prev, js_content: templateContent }));
      extractSignatureSpots(templateContent);
    }
  };

  const handleContractTypeChange = (newType) => {
    const currentContent = formData.js_content;
    
    // Extract the main content from current template
    let extractedContent = '';
    const contentMatch = currentContent.match(/<div className="content"[^>]*>([\s\S]*?)<div style={{margin: '20px 0'/);
    if (contentMatch) {
      extractedContent = contentMatch[1].trim();
    }
    
    const newTemplate = createTemplateFromContent(extractedContent, newType);
    setFormData(prev => ({ ...prev, contract_type: newType, js_content: newTemplate }));
    extractSignatureSpots(newTemplate);
  };

  const insertAtCursor = (text) => {
    const textarea = editorRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = 
        formData.js_content.substring(0, start) + 
        text + 
        formData.js_content.substring(end);
      
      setFormData(prev => ({ ...prev, js_content: newContent }));
      
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + text.length;
        textarea.focus();
      }, 10);
    }
  };

  const handleInsertSignatureSpot = () => {
    if (formData.contract_type === 'simple') {
      alert('Simple contracts use automatic agreement buttons. No signature spots needed.');
      return;
    }
    
    const spotId = `spot_${Date.now()}`;
    const spotLabel = prompt('Enter signature label:', 'Employee Signature');
    if (spotLabel) {
      const spotText = `{{SIGNATURE_SPOT:${spotLabel}:${spotId}}}`;
      insertAtCursor(spotText);
      setSignatureSpots(prev => [...prev, { id: spotId, label: spotLabel, type: 'signature' }]);
    }
    setInsertMenuAnchor(null);
  };

  const handleInsertVariable = (variable) => {
    insertAtCursor(`{{${variable}}}`);
    setInsertMenuAnchor(null);
  };

  const renderPreview = () => {
    let content = formData.js_content;
    
    // Replace variables with sample data
    const replacements = {
      'CONTRACT_TITLE': 'Employment Agreement',
      'COMPANY_NAME': 'Sample Company Inc.',
      'SIGNATURE_DATE': new Date().toLocaleDateString(),
      'EMPLOYEE_NAME': 'John Doe',
      'MANAGER_NAME': 'Jane Smith',
      'START_DATE': new Date().toLocaleDateString(),
      'DEPARTMENT': 'Engineering',
      'POSITION': 'Software Developer',
      'SALARY': '$75,000',
      'END_DATE': 'N/A'
    };

    Object.entries(replacements).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });

    // Replace signature spots with preview components
    content = content.replace(/\{\{SIGNATURE_SPOT:([^:]+):([^}]+)\}\}/g, (match, label, id) => {
      return `<div style="border: 2px dashed #ccc; padding: 20px; text-align: center; margin: 10px 0; border-radius: 5px; background-color: #fafafa;">
        <div style="color: #666; font-size: 12px; margin-bottom: 10px; font-weight: bold;">${label}</div>
        <div style="height: 40px; border-bottom: 1px solid #333; margin: 10px 0;"></div>
        <div style="font-size: 10px; color: #999;">Signature Line</div>
        <div style="font-size: 10px; color: #999; margin-top: 5px;">Date: ___________</div>
      </div>`;
    });

    // Replace simple agreement button
    content = content.replace(/\{\{SIMPLE_AGREEMENT_BUTTON\}\}/g, 
      `<button style="background-color: #007bff; color: white; padding: 12px 24px; border: none; border-radius: 5px; font-size: 16px; cursor: pointer; font-weight: bold;">
        ✓ I Agree to These Terms
      </button>`
    );

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: content }}
        style={{
          padding: '20px',
          backgroundColor: 'white',
          minHeight: '400px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          fontSize: '14px'
        }}
      />
    );
  };

  const handleSubmit = async () => {
  const { name, js_content, expires_after_days } = formData;

  // Basic validation checks
  if (!name.trim()) {
    alert('Template Name is required.');
    return;
  }

  if (!js_content.trim()) {
    alert('Contract Content is required.');
    return;
  }

  // Explicitly validate expires_after_days to be integer or null
  if (expires_after_days !== '') {
    const expiresDaysNum = parseInt(expires_after_days, 10);
    if (isNaN(expiresDaysNum) || expiresDaysNum <= 0) {
      alert('Expires After Days must be a positive integer or left empty.');
      return;
    }
  }

  try {
    setLoading(true);

    const dataToSend = {
      ...formData,
      expires_after_days: formData.expires_after_days === '' ? null : parseInt(formData.expires_after_days, 10),
      metadata: {
        contract_type: formData.contract_type,
        signature_spots: signatureSpots
      }
    };

    if (template) {
      await client.contracts.updateTemplate(template.id, dataToSend);
    } else {
      await client.contracts.createTemplate(dataToSend);
    }
    
    onSave();
    onClose();
  } catch (error) {
    console.error('Failed to save template:', error);
    alert('Failed to save template');
  } finally {
    setLoading(false);
  }
};


  const contractVariables = [
    'CONTRACT_TITLE',
    'COMPANY_NAME', 
    'EMPLOYEE_NAME',
    'MANAGER_NAME',
    'SIGNATURE_DATE',
    'START_DATE',
    'END_DATE',
    'DEPARTMENT',
    'POSITION',
    'SALARY'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {template ? 'Edit Template' : 'Create Contract Template'}
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title={showToolbar ? 'Hide Toolbar' : 'Show Toolbar'}>
              <IconButton onClick={() => setShowToolbar(!showToolbar)} size="small">
                {showToolbar ? <HideIcon /> : <ViewIcon />}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const modes = ['split', 'preview', 'code'];
                const nextMode = modes[(modes.indexOf(previewMode) + 1) % modes.length];
                setPreviewMode(nextMode);
              }}
            >
              {previewMode === 'split' ? 'Split' : previewMode === 'preview' ? 'Preview' : 'Code'}
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Form Fields */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Contract Type</InputLabel>
                <Select
                  value={formData.contract_type}
                  onChange={(e) => handleContractTypeChange(e.target.value)}
                >
                  <MenuItem value="simple">Simple (Click to Agree)</MenuItem>
                  <MenuItem value="complex">Complex (Signature Required)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Expires After (Days)"
                type="number"
                size="small"
                value={formData.expires_after_days}
                onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                    setFormData(prev => ({ ...prev, expires_after_days: value }));
                    }
                }}
                helperText="Leave empty for no expiration"
                />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Upload Section */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Card sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Start Options
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={processingDocument}
              >
                Upload Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoIcon />}
                onClick={handlePasteText}
              >
                Paste & Convert Text
              </Button>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => {
                  const newTemplate = getDefaultTemplate(formData.contract_type);
                  setFormData(prev => ({ ...prev, js_content: newTemplate }));
                  extractSignatureSpots(newTemplate);
                }}
              >
                Reset to Template
              </Button>
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
            />
            {processingDocument && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <CircularProgress size={16} />
                <Typography variant="caption">Processing document...</Typography>
              </Box>
            )}
          </Card>
        </Box>

        {/* Toolbar */}
        {showToolbar && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => setInsertMenuAnchor(e.currentTarget)}
                >
                  Insert
                </Button>
                <Divider orientation="vertical" flexItem />
                <Chip 
                  icon={formData.contract_type === 'simple' ? <AutoIcon /> : <PersonIcon />} 
                  label={formData.contract_type === 'simple' ? 'Simple Agreement' : `${signatureSpots.length} Signature Spots`}
                  variant="outlined"
                  size="small"
                />
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => navigator.clipboard.writeText(formData.js_content)}
                >
                  Copy
                </Button>
                {formData.contract_type === 'complex' && (
                  <Button
                    size="small"
                    onClick={() => setShowSpotDetails(!showSpotDetails)}
                  >
                    {showSpotDetails ? 'Hide' : 'Show'} Spots
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Main Editor Area */}
        <Box sx={{ height: '600px', display: 'flex' }}>
          {/* Code Editor */}
          {(previewMode === 'code' || previewMode === 'split') && (
            <Box sx={{ 
              flex: previewMode === 'split' ? 1 : 2, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              borderRight: previewMode === 'split' ? '1px solid #e0e0e0' : 'none'
            }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Contract Template Code
              </Typography>
              <TextField
                ref={editorRef}
                multiline
                fullWidth
                value={formData.js_content}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, js_content: e.target.value }));
                  extractSignatureSpots(e.target.value);
                }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '12px'
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
                placeholder="Enter your contract template HTML/React code here..."
                variant="outlined"
              />
            </Box>
          )}

          {/* Live Preview */}
          {(previewMode === 'preview' || previewMode === 'split') && (
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflow: 'auto', 
              backgroundColor: '#f8f9fa'
            }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Live Preview
              </Typography>
              <Paper elevation={2} sx={{ minHeight: '500px', overflow: 'auto' }}>
                {renderPreview()}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Signature Spots Details (only for complex contracts) */}
        {showSpotDetails && formData.contract_type === 'complex' && signatureSpots.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Signature Spots ({signatureSpots.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {signatureSpots.map((spot) => (
                    <ListItem key={spot.id}>
                      <ListItemText 
                        primary={spot.label}
                        secondary={`ID: ${spot.id}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          size="small"
                          onClick={() => {
                            const newContent = formData.js_content.replace(
                              `{{SIGNATURE_SPOT:${spot.label}:${spot.id}}}`,
                              ''
                            );
                            setFormData(prev => ({ ...prev, js_content: newContent }));
                            setSignatureSpots(prev => prev.filter(s => s.id !== spot.id));
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Settings */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.requires_resignation_on_update}
                onChange={(e) => setFormData(prev => ({ ...prev, requires_resignation_on_update: e.target.checked }))}
              />
            }
            label={`Require re-${formData.contract_type === 'simple' ? 'agreement' : 'signing'} on template updates`}
          />
        </Box>

        {/* Insert Menu */}
        <Menu
          anchorEl={insertMenuAnchor}
          open={Boolean(insertMenuAnchor)}
          onClose={() => setInsertMenuAnchor(null)}
          MenuListProps={{ dense: true }}
        >
          {formData.contract_type === 'complex' && (
            <>
              <MenuItemComponent onClick={handleInsertSignatureSpot}>
                <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                <Typography variant="body2">Signature Spot</Typography>
              </MenuItemComponent>
              <Divider />
            </>
          )}
          {formData.contract_type === 'simple' && (
            <>
              <MenuItemComponent onClick={() => handleInsertVariable('SIMPLE_AGREEMENT_BUTTON')}>
                <AutoIcon sx={{ mr: 1, fontSize: 16 }} />
                <Typography variant="body2">Agreement Button</Typography>
              </MenuItemComponent>
              <Divider />
            </>
          )}
          {contractVariables.map((variable) => (
            <MenuItemComponent 
              key={variable}
              onClick={() => handleInsertVariable(variable)}
            >
              <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2">{variable.replace(/_/g, ' ')}</Typography>
            </MenuItemComponent>
          ))}
        </Menu>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedContractTemplateDialog;